import React from "react";

/**
 * Viewport helpers. This system styles inline and ships no stylesheet, so the
 * two layouts that genuinely change SHAPE (nav → drawer, sidebar → top bar)
 * read the width here instead of a media query.
 *
 * Breakpoints mirror tokens/breakpoints.css and the app's Tailwind config,
 * including the `nav` screen at 992px that `isNavMobile` reads.
 *
 * `useViewport` is the hook the bundled components use internally.
 * `Viewport` is the render-prop wrapper consumers can reach — only names
 * starting with a capital letter are exposed on the design-system namespace.
 */
export function useViewport() {
  const get = () => (typeof window === "undefined" ? 1280 : window.innerWidth);
  const [w, setW] = React.useState(get);
  React.useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  // `isNavMobile` is the mobile-SHELL break (T12-M1), 992px, and it is separate
  // from `isMobile` on purpose. The two answer different questions: `isMobile`
  // is "is this a phone-shaped column" (the composer, the landing hero, the
  // chat's one-pane rule) and stays at 768. `isNavMobile` is "does this session
  // navigate with the bottom tab bar", which replaces both the Navbar's drawer
  // and the dashboard's black top bar. Widening `isMobile` to 992 would have
  // dragged three unrelated layouts across with it.
  return {
    width: w,
    isMobile: w < 768,
    isNavMobile: w < 992,
    isTablet: w >= 768 && w < 1024,
    isDesktop: w >= 1024,
  };
}

export function Viewport({ children }) {
  const vp = useViewport();
  return typeof children === "function" ? children(vp) : children;
}
